import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import AppModal from '../../../../../generic/ui/Modal/AppModal'
import ConfirmDialog from '../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../generic/grid-table'
import { GridColumn } from '../../../../../generic/grid-table/types/grid.types'
import PalletForm from './components/PalletForm'
import useTableData from '../../../../../generic/universal-table/hooks/useTableData'
import useTableEditing from '../../../../../generic/universal-table/hooks/useTableEditing'
import useTableFilters from '../../../../../generic/universal-table/hooks/useTableFilters'
import { Tables } from '../../../../../modules/API/Tables/Tables'
import { IPackageTableItem } from '../../../../../modules/API/Tables/models/packages'
import { IPalletTableItem } from '../../../../../modules/API/Tables/models/pallets'
import { palletTableService } from './services/palletTable.service'
import { palletTableColumns } from './configs/palletTable.columns'
import { palletTableFormFields } from './configs/palletTable.form'
import { palletTableAdapter } from './adapters/palletTable.adapter'
import {
  PalletCreatePayload,
  PalletUiRow,
  PalletUpdatePayload,
} from './types/palletTable.types'
import { ColumnDefinition } from '../../../../../generic/universal-table/types/column.types'
import { FormFieldDefinition } from '../../../../../generic/universal-table/types/form.types'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'
import { IClientItem } from '../../../../../modules/API/Defaults/models/clients'
import { Defaults } from '../../../../../modules/API/Defaults/defaults'
import useExportToExcel from '../../../../../generic/hooks/useExportToExcel'
import columnsToExcel from '../../../../../generic/universal-table/utils/columnsToExcel'

const ACCESSORY_PACK_TYPE = 'א'

const EMPTY_SELECT_LABEL = 'ריק'

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  color: tomato;
  direction: rtl;
  padding: 0.5em 1em;
`

const PalletTableModule = () => {
  const {
    rows,
    isLoading,
    error,
    createRow,
    updateRow,
    removeRow,
  } = useTableData<PalletUiRow, PalletCreatePayload, PalletUpdatePayload>(
    palletTableService,
    palletTableAdapter.getRowId
  )

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<PalletUiRow>()

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<PalletUiRow>(rows)

  const [growers, setGrowers] = useState<IClientItem[]>([])
  const [packages, setPackages] = useState<IPackageTableItem[]>([])
  const [palletsFromApi, setPalletsFromApi] = useState<IPalletTableItem[]>([])

  useEffect(() => {
    const fetchData = async () => {
    try {
      const [growersRes, packagesRes, palletsRes]: [IClientItem[], IPackageTableItem[], IPalletTableItem[]] = await Promise.all([
        Defaults.getClientsGo(),
        Tables.getPackageList(),
        Tables.getPalletList(),
      ])
      setGrowers(growersRes.filter(g => g.ClientType === '3'))
      setPackages(packagesRes)
      setPalletsFromApi(palletsRes)
    } catch (error) {
      handleNotifications('שגיאה בטעינת הנתונים', {type: 'error'})
    }
  }
  fetchData()
  }, [])

  const growerOptions = useMemo(
    () => [{ value: 0, label: 'ללא ספק' }, ...growers.map((g) => ({ value: g.ClientID, label: g.ClientName }))],
    [growers]
  )

  const accessoryOptions = useMemo(() => {
    const filtered = packages.filter(
      (p) => (p.Pack_Type?.trim() ?? '') === ACCESSORY_PACK_TYPE
    )
    return [
      { value: 0, label: EMPTY_SELECT_LABEL },
      ...filtered.map((p) => ({ value: p.PackID, label: p.Pack_Name })),
    ]
  }, [packages])

  const mainPalletOptions = useMemo(
    () => [
      { value: 0, label: EMPTY_SELECT_LABEL },
      ...palletsFromApi.map((p) => ({
        value: p.Pallete_Type,
        label: p.Pallete_Desc?.trim() || String(p.Pallete_Type),
      })),
    ],
    [palletsFromApi]
  )

  const resolveAccessoryLabel = useCallback(
    (id: unknown) =>
      accessoryOptions.find((o) => o.value === id)?.label ?? String(id),
    [accessoryOptions]
  )

  const resolveMainPalletLabel = useCallback(
    (id: unknown) =>
      mainPalletOptions.find((o) => o.value === id)?.label ?? String(id),
    [mainPalletOptions]
  )

  const enrichedColumns = useMemo<ColumnDefinition<PalletUiRow>[]>(
    () =>
      palletTableColumns.map((col) => {
        if (col.key === 'SupplierID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              growerOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        if (col.key === 'Main_PalleteID') {
          return {
            ...col,
            labelResolver: resolveMainPalletLabel,
          }
        }
        if (col.key === 'Accessory1' || col.key === 'Accessory2' || col.key === 'Accessory3') {
          return {
            ...col,
            labelResolver: resolveAccessoryLabel,
          }
        }
        return col
      }),
    [growerOptions, resolveAccessoryLabel, resolveMainPalletLabel]
  )

  const enrichedFormFields = useMemo<FormFieldDefinition<PalletUiRow>[]>(
    () =>
      palletTableFormFields.map((field) => {
        if (field.name === 'SupplierID') {
          return { ...field, getOptions: () => growerOptions }
        }
        if (field.name === 'Main_PalleteID') {
          return { ...field, getOptions: () => mainPalletOptions }
        }
        if (field.name === 'Accessory1' || field.name === 'Accessory2' || field.name === 'Accessory3') {
          return { ...field, getOptions: () => accessoryOptions }
        }
        return field
      }),
    [growerOptions, accessoryOptions, mainPalletOptions]
  )

  // Grid view reuses the enriched columns and layers on richer renderers.
  const gridColumns = useMemo<GridColumn<PalletUiRow>[]>(
    () =>
      enrichedColumns.map((col): GridColumn<PalletUiRow> => {
        if (col.key === 'Pallete_Type') return { ...col, cellRendererType: 'num' }
        if (col.key === 'Pallete_Price') {
          return { ...col, cellRendererType: 'money', currencySuffix: '₪' }
        }
        return col
      }),
    [enrichedColumns]
  )

  const excelColumns = useMemo(() => columnsToExcel(enrichedColumns), [enrichedColumns])
  const { handleExport } = useExportToExcel({ columns: excelColumns, fileName: 'pallets', rtl: true })

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleFormSubmit = useCallback(
    async (values: Partial<PalletUiRow>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          const id = palletTableAdapter.getRowId(selectedRow)
          const payload = palletTableAdapter.toUpdatePayload({
            ...selectedRow,
            ...values,
          } as PalletUiRow)
          await updateRow(id, payload)
        } else {
          const payload = palletTableAdapter.toCreatePayload(values as PalletUiRow)
          await createRow(payload)
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createRow, updateRow, handleCloseForm]
  )

  const formTitle = editingMode === 'edit' ? 'עריכת משטח' : 'הוספת משטח חדש'

  return (
    <TableSection>
      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={PalletForm}
          childrenProps={{
            fields: enrichedFormFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size="md"
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title="מחיקת משטח"
        message="האם למחוק את המשטח? פעולה זו אינה הפיכה."
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() =>
          handleConfirmDelete((row) => removeRow(palletTableAdapter.getRowId(row)))
        }
      />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <GridTable<PalletUiRow>
        title="משטחים"
        count={filteredRows.length}
        columns={gridColumns}
        rows={filteredRows}
        getRowId={palletTableAdapter.getRowId}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder="חיפוש משטח…"
            onExport={() => handleExport(filteredRows)}
            onAdd={handleOpenCreate}
            addLabel="הוסף משטח"
          />
        }
      />
    </TableSection>
  )
}

export default PalletTableModule
